import { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import {
  CreditCard, Download, CheckCircle2, Clock, AlertCircle, Receipt,
  ArrowUpRight, Globe, Smartphone, Shield, Loader2, ExternalLink,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

const statusConfig: Record<string, { label: string; icon: any; class: string }> = {
  paid: { label: "Pago", icon: CheckCircle2, class: "bg-accent/15 text-accent border-accent/30" },
  open: { label: "Pendente", icon: Clock, class: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" },
  draft: { label: "Rascunho", icon: Clock, class: "bg-muted/15 text-muted-foreground border-muted/30" },
  uncollectible: { label: "Falhou", icon: AlertCircle, class: "bg-destructive/15 text-destructive border-destructive/30" },
  void: { label: "Cancelada", icon: AlertCircle, class: "bg-muted/15 text-muted-foreground border-muted/30" },
};

const subStatusLabels: Record<string, { label: string; class: string }> = {
  active: { label: "Ativo", class: "bg-accent/15 text-accent border-accent/30" },
  trialing: { label: "Trial", class: "bg-primary/15 text-primary border-primary/30" },
  past_due: { label: "Atrasado", class: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" },
  canceled: { label: "Cancelado", class: "bg-destructive/15 text-destructive border-destructive/30" },
  incomplete: { label: "Incompleto", class: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" },
  unpaid: { label: "Não pago", class: "bg-destructive/15 text-destructive border-destructive/30" },
};

function formatDate(ts: number) {
  return new Date(ts * 1000).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function DashboardBilling() {
  const { user } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);

  const { data: billing, isLoading, refetch } = useQuery({
    queryKey: ["billing-info", user?.id],
    queryFn: async () => {
      // Fire sync in background, don't block billing info
      supabase.functions.invoke("sync-subscription").catch(() => {});

      const { data, error } = await supabase.functions.invoke("get-billing-info");
      if (error) throw error;
      return data as {
        plan: {
          name: string;
          basePrice: number;
          baseNumbers: number;
          extraNumbers: number;
          extraNumberUnitPrice: number;
          totalNumbers: number;
          globalNetworkActive: boolean;
          globalNetworkUnitPrice: number;
          globalNetworkQty: number;
          status: string;
          currentPeriodEnd: number;
          currentPeriodStart: number;
          cancelAtPeriodEnd: boolean;
        } | null;
        invoices: {
          id: string;
          date: number;
          amount: number;
          status: string;
          currency: string;
          hostedUrl: string | null;
          pdfUrl: string | null;
        }[];
        paymentMethod: {
          brand: string;
          last4: string;
          expMonth: number;
          expYear: number;
        } | null;
        profile: any;
      };
    },
    enabled: !!user,
  });

  const handleManageBilling = async () => {
    // If user has no stripe_customer_id, redirect to checkout to create one
    if (!billing?.profile?.stripe_customer_id) {
      window.location.href = "/checkout";
      return;
    }
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-portal-session");
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      console.error(err);
      toast.error("Erro ao abrir portal de pagamento.");
    } finally {
      setPortalLoading(false);
    }
  };

  const plan = billing?.plan;
  const invoices = billing?.invoices || [];
  const pm = billing?.paymentMethod;

  const totalMonthly = plan
    ? plan.basePrice +
      plan.extraNumbers * plan.extraNumberUnitPrice +
      (plan.globalNetworkActive ? plan.globalNetworkQty * plan.globalNetworkUnitPrice : 0)
    : 0;

  const subStatus = plan ? subStatusLabels[plan.status] || subStatusLabels.active : null;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">Faturamento</h1>
          <p className="text-muted-foreground text-sm mt-1">Gerencie seu plano, add-ons e histórico de pagamentos.</p>
        </div>
        <Button asChild variant="outline" className="gap-2">
          <Link to="/dashboard/upgrade">
            <ArrowUpRight className="h-4 w-4" />
            Atualizar Plano
          </Link>
        </Button>
      </div>

      {/* Current Plan Card */}
      {plan ? (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="glass-card gradient-border border-warmz-glass-border/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  {subStatus && (
                    <Badge className={`${subStatus.class} mb-2`}>{subStatus.label}</Badge>
                  )}
                  <CardTitle className="font-display text-xl">{plan.name}</CardTitle>
                  <CardDescription>
                    {plan.status === "trialing" ? (() => {
                      const daysLeft = Math.max(0, Math.ceil((plan.currentPeriodEnd * 1000 - Date.now()) / (1000 * 60 * 60 * 24)));
                      return `Trial encerra em ${daysLeft} dia${daysLeft !== 1 ? "s" : ""} (${formatDate(plan.currentPeriodEnd)})`;
                    })() : plan.cancelAtPeriodEnd
                      ? `Cancela em ${formatDate(plan.currentPeriodEnd)}`
                      : `Renovação automática em ${formatDate(plan.currentPeriodEnd)}`}
                  </CardDescription>
                </div>
                <div className="text-right">
                  <div className="font-display text-3xl font-bold gradient-text-fire">${totalMonthly}</div>
                  <span className="text-xs text-muted-foreground">/mês</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Separator className="mb-4 bg-warmz-glass-border/30" />
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/40">
                  <Smartphone className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">Números</p>
                    <p className="text-sm font-semibold text-foreground">
                      {plan.baseNumbers} base{plan.extraNumbers > 0 ? ` + ${plan.extraNumbers} extras` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/40">
                  <Globe className="h-5 w-5 text-accent" />
                  <div>
                    <p className="text-xs text-muted-foreground">Rede Global</p>
                    <p className="text-sm font-semibold text-foreground">
                      {plan.globalNetworkActive ? `Ativa (${plan.globalNetworkQty} chips)` : "Inativa"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/40">
                  <Shield className="h-5 w-5 text-yellow-400" />
                  <div>
                    <p className="text-xs text-muted-foreground">Proteção</p>
                    <p className="text-sm font-semibold text-foreground">Anti-ban ativo</p>
                  </div>
                </div>
              </div>

              {/* Price breakdown */}
              <div className="mt-5 p-4 rounded-xl bg-secondary/30 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Plano base ({plan.baseNumbers} números)</span>
                  <span className="text-foreground">${plan.basePrice}</span>
                </div>
                {plan.extraNumbers > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{plan.extraNumbers} números extras × ${plan.extraNumberUnitPrice}</span>
                    <span className="text-foreground">${plan.extraNumbers * plan.extraNumberUnitPrice}</span>
                  </div>
                )}
                {plan.globalNetworkActive && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rede Global ({plan.globalNetworkQty} chips × ${plan.globalNetworkUnitPrice})</span>
                    <span className="text-foreground">${plan.globalNetworkQty * plan.globalNetworkUnitPrice}</span>
                  </div>
                )}
                <Separator className="bg-warmz-glass-border/20" />
                <div className="flex justify-between font-bold">
                  <span className="text-foreground">Total mensal</span>
                  <span className="gradient-text-fire">${totalMonthly}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="py-10 text-center">
              <p className="text-muted-foreground mb-4">Você ainda não possui um plano ativo.</p>
              <Button asChild>
                <Link to="/checkout">Escolher Plano</Link>
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Payment Method */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card className="glass-card border-warmz-glass-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-display">Método de Pagamento</CardTitle>
          </CardHeader>
          <CardContent>
            {pm ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-10 rounded-lg bg-secondary/60 flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground capitalize">
                      •••• •••• •••• {pm.last4}
                    </p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {pm.brand} · Expira {String(pm.expMonth).padStart(2, "0")}/{pm.expYear}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleManageBilling} disabled={portalLoading}>
                  {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Alterar"}
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Nenhum método de pagamento cadastrado.</p>
                <Button variant="outline" size="sm" onClick={handleManageBilling} disabled={portalLoading}>
                  {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Adicionar"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Invoices */}
      {invoices.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="glass-card border-warmz-glass-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-display">Histórico de Faturas</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-warmz-glass-border/20">
                {invoices.map((inv) => {
                  const cfg = statusConfig[inv.status] || statusConfig.paid;
                  return (
                    <div key={inv.id} className="flex items-center justify-between px-6 py-4 hover:bg-warmz-card-hover/30 transition-colors">
                      <div className="flex items-center gap-4">
                        <Receipt className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium text-foreground">{inv.id}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(inv.date)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge className={`text-xs border ${cfg.class}`}>
                          <cfg.icon className="h-3 w-3 mr-1" />
                          {cfg.label}
                        </Badge>
                        <span className="text-sm font-semibold text-foreground w-16 text-right">
                          ${inv.amount.toFixed(2)}
                        </span>
                        {inv.pdfUrl && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" asChild title="Download PDF">
                            <a href={inv.pdfUrl} target="_blank" rel="noopener noreferrer">
                              <Download className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
